const TOKEN_STARTER = '@';
const ACTIVE_TOKEN_VALUE = -1;

// A few simple internal tools
// ====================================================
const _clamp = (val, a, b) => {
  if (val < a) {
    return a;
  } else if (val > b) {
    return b;
  } else {
    return val;
  }
};

const _tokenOverlaps = (token, posA, posB) => {
  return posB > token.start && posA < token.start + token.len;
};

const _tokenText = (text, token) => {
  return text.slice(token.start, token.start + token.len);
};

// remove the part of the indicated substring that overlaps the token
// then insert the indicated string into the position that was modified
// Example:
// "123 @token abc" <- token is '@token'
// replace the substring "ken ab" -> "X"
// result: "123 @toXc" <- new token is '@toX'

const _modifyToken = (oldtext, token, replaceA, replaceB, strInsert) => {
  // get the original token text
  const tokenText = _tokenText(oldtext, token);

  // find the substring within the token text to be removed
  const tokenReplaceA = _clamp(replaceA - token.start, 0, token.len);
  const tokenReplaceB = _clamp(replaceB - token.start, 0, token.len);

  // replace the substring with 'strInsert'
  const newTokenText = [
    tokenText.slice(0, tokenReplaceA),
    strInsert,
    tokenText.slice(tokenReplaceB),
  ].join('');

  const newToken = token;
  newToken.start = Math.min(replaceA, token.start);
  newToken.len = newTokenText.length;
  return newToken;
};

// Assuming there is a single insertion, deletion, or replacement, find it.
// In the case of ABC -> ABBC, will bias toward the insertion being the 1st B
const _simpleDiff = (oldtext, newtext) => {
  let maxindexOld;
  let maxindexNew;
  let maxindex;

  // don't try to match beyond the length of the string
  maxindexOld = oldtext.length;
  maxindexNew = newtext.length;
  maxindex = Math.min(maxindexOld, maxindexNew);

  // match starting from the right
  let rightMatchLen = maxindex;
  for (let i = 0; i < maxindex; i += 1) {
    const charOld = oldtext[oldtext.length - 1 - i];
    const charNew = newtext[newtext.length - 1 - i];

    if (charOld !== charNew) {
      rightMatchLen = i;
      break;
    }
  }

  // don't try to match from the left into the already-matched part on the right
  maxindexOld = oldtext.length - rightMatchLen;
  maxindexNew = newtext.length - rightMatchLen;
  maxindex = Math.min(maxindexOld, maxindexNew);

  // match starting from the left
  let leftMatchLen = maxindex;
  for (let i = 0; i < maxindex; i += 1) {
    const charOld = oldtext[i];
    const charNew = newtext[i];

    if (charOld !== charNew) {
      leftMatchLen = i;
      break;
    }
  }

  return {
    leftMatchLen,
    rightMatchLen,
  };
};

// Equality check for tokenized strings
// ====================================================
const tokenizedStringsAreEqual = (oldtext, oldtokens, newtext, newtokens) => {
  if (oldtext !== newtext) {
    return false;
  }
  if (oldtokens.length !== newtokens.length) {
    return false;
  }

  for (let i = 0; i < oldtokens.length; i += 1) {
    const tokensEqual =
      oldtokens[i].value === newtokens[i].value &&
      oldtokens[i].start === newtokens[i].start &&
      oldtokens[i].len === newtokens[i].len;
    if (!tokensEqual) {
      return false;
    }
  }

  return true;
};

// NOTE: assumes tokens do not overlap!
// Returns array of substrings:
// { start, len, text, value } <- value is either a number or null
// ====================================================
const splitTokenizedText = (rawtext, tokens) => {
  // if there are no tokens, push a single chunk encompassing entire string
  if (!tokens || tokens.length === 0) {
    const chunk = {
      start: 0,
      len: rawtext.length,
      text: rawtext,
      value: null,
      type: null,
    };
    return [chunk];
  }

  tokens.slice().sort((tokenA, tokenB) => {
    return tokenA.start - tokenB.start;
  });

  const substrings = [];

  // push text before the first token
  let token = tokens[0];

  let chunk = {
    start: 0,
    len: token.start,
    text: rawtext.slice(0, token.start),
    value: null,
    type: null,
  };
  if (chunk.len > 0) {
    substrings.push(chunk);
  }

  for (let i = 0; i < tokens.length; i += 1) {
    token = tokens[i];

    // push the current token
    chunk = {
      start: token.start,
      len: token.len,
      text: _tokenText(rawtext, token),
      value: token.value,
      type: token.type,
    };
    if (chunk.len > 0) {
      substrings.push(chunk);
    }

    // push the text after this token but before the next (or up to end of string)
    const isLastToken = i === tokens.length - 1;
    const padStart = token.start + token.len;
    const padEnd = isLastToken ? rawtext.length : tokens[i + 1].start;

    chunk = {
      start: padStart,
      len: padEnd - padStart,
      text: rawtext.slice(padStart, padEnd),
      value: null,
      type: token.type,
    };
    if (chunk.len > 0) {
      substrings.push(chunk);
    }
  }

  return substrings;
};

// Transforms a tokenized string by passing each token through transformer
// Transformer is: (tokentext, tokenvalue) => newtext
// Returns { newtext, newtokens }
// ====================================================
const transformTokens = (rawtext, tokens, transformer) => {
  const substrings = splitTokenizedText(rawtext, tokens);

  const newtextArr = [];
  const newtokens = [];
  let currentIndex = 0;

  for (let i = 0; i < substrings.length; i += 1) {
    const { text, value, type } = substrings[i];

    let newSubstringText = text;
    if (value !== null) {
      newSubstringText = transformer(text, value);
      newtokens.push({
        start: currentIndex,
        len: newSubstringText.length,
        value,
        type,
      });
    }
    newtextArr.push(newSubstringText);
    currentIndex += newSubstringText.length;
  }

  return {
    newtext: newtextArr.join(''),
    newtokens,
  };
};

// Carry out a simple diff and update metadata/activeToken
// Returns object { metadata, activeToken }
// ====================================================
const updateMetadata = (oldtext, newtext, metadata, activeToken) => {
  if (oldtext === newtext) {
    return { metadata, activeToken };
  }

  // Step 1: determine all the necessary diff details
  // --------------------------------------------------------
  const { leftMatchLen, rightMatchLen } = _simpleDiff(oldtext, newtext);

  const a2 = oldtext.substring(leftMatchLen, oldtext.length - rightMatchLen);
  const b2 = newtext.substring(leftMatchLen, newtext.length - rightMatchLen);

  // console.log(`[${a1}] [${a2}] [${a3}]`);
  // console.log(`[${b1}] [${b2}] [${b3}]`);
  // console.log(`Replaced '${a2}' -> '${b2}'`);

  // positions in oldtext of the substring being replaced
  const replaceA = leftMatchLen;
  const replaceB = oldtext.length - rightMatchLen;

  // Step 2: construct new metadata by shifting or dropping tokens
  // --------------------------------------------------------
  const newMetadata = [];
  const removedTokens = [];

  for (let i = 0; i < metadata.length; i += 1) {
    const token = metadata[i];

    const tokenContainsReplace = _tokenOverlaps(token, replaceA, replaceB);
    const tokenIsAfterReplace = replaceB <= token.start;

    // shift token if it is after the replace
    if (tokenIsAfterReplace) {
      token.start += b2.length - a2.length;
    }

    // only keep token if it does not overlap the replace
    if (!tokenContainsReplace) {
      newMetadata.push(token);
    } else {
      removedTokens.push(token);
    }
  }

  // get a token to be re-activated
  let reactivatedToken = null;
  for (let i = 0; i < removedTokens.length; i += 1) {
    const modifiedToken = _modifyToken(
      oldtext,
      removedTokens[i],
      replaceA,
      replaceB,
      b2,
    );

    const modifiedTokenText = _tokenText(newtext, modifiedToken);
    if (modifiedTokenText.startsWith(TOKEN_STARTER)) {
      reactivatedToken = modifiedToken;
    }
  }

  // Step 3: update activeToken
  // --------------------------------------------------------
  let newActiveToken = null;

  // start a new active token if TOKEN_STARTER inserted and no non-whitespace before
  let leadingNonWhitespace = false;
  if (replaceA > 0) {
    const leadingChar = oldtext[replaceA - 1];
    leadingNonWhitespace = leadingChar === leadingChar.trim();
  }

  if (b2 === TOKEN_STARTER && !leadingNonWhitespace) {
    newActiveToken = {
      start: replaceA,
      len: 1,
      value: ACTIVE_TOKEN_VALUE,
    };
  }

  // if there is a token to be reactivated, do it
  else if (reactivatedToken) {
    newActiveToken = reactivatedToken;
    newActiveToken.value = ACTIVE_TOKEN_VALUE;
  }

  // if there is no active token to modify, do nothing more
  else if (!activeToken) {
    newActiveToken = null;
  }

  // modify existing active token
  else {
    const tokenIsAfterReplace = replaceB <= activeToken.start;
    const tokenIsStrictlyBeforeReplace =
      replaceA > activeToken.start + activeToken.len;

    // if editing away from active token, deactivate it
    if (tokenIsAfterReplace || tokenIsStrictlyBeforeReplace) {
      newActiveToken = null;
    }

    // otherwise, need to modify the active token and possibly remove it
    else {
      newActiveToken = _modifyToken(
        oldtext,
        activeToken,
        replaceA,
        replaceB,
        b2,
      );

      // deactivate if got rid of TOKEN_STARTER
      const newActiveTokenText = _tokenText(newtext, newActiveToken);
      if (!newActiveTokenText.startsWith(TOKEN_STARTER)) {
        newActiveToken = null;
      }
    }
  }

  return {
    metadata: newMetadata,
    activeToken: newActiveToken,
  };
};

// Convert activeToken into an actual token, then reset the active token
// returns { metadata, activeToken, newtext, cursorPosition } OR null
// ====================================================
const convertActiveToken = (
  rawtext,
  metadata,
  activeToken,
  tokenText,
  tokenValue,
  tokenType,
) => {
  if (!activeToken) {
    return null;
  }

  const correctedTokenText = `${TOKEN_STARTER}${tokenText}`;

  const textBeforeToken = rawtext.slice(0, activeToken.start);
  const textAfterToken = rawtext.slice(activeToken.start + activeToken.len);

  const spaceExistsAfterToken =
    textAfterToken.length > 0 && textAfterToken[0] === ' ';

  const newtext = [
    textBeforeToken,
    correctedTokenText + (spaceExistsAfterToken ? '' : ' '),
    textAfterToken,
  ].join('');
  const replaced = updateMetadata(rawtext, newtext, metadata, null);

  const newToken = {
    start: activeToken.start,
    len: correctedTokenText.length,
    value: tokenValue,
    type: tokenType,
  };
  replaced.metadata.push(newToken);

  return {
    metadata: replaced.metadata,
    activeToken: null,
    newtext,
    cursorPosition: newToken.start + newToken.len + 1,
  };
};

// Replace the indicated range with TOKEN_STARTER, and prepare a new active token
// returns { metadata, activeToken, newtext, cursorPosition }
// ====================================================
const insertTokenStarter = (rawtext, metadata, selrange) => {
  const textBeforeSel = rawtext.slice(0, selrange[0]);
  const textAfterSel = rawtext.slice(selrange[1]);

  const newtext = [textBeforeSel, TOKEN_STARTER, textAfterSel].join('');
  const replaced = updateMetadata(rawtext, newtext, metadata, null);

  const newActiveToken = {
    start: selrange[0],
    len: TOKEN_STARTER.length,
    value: ACTIVE_TOKEN_VALUE,
  };
  return {
    metadata: replaced.metadata,
    activeToken: newActiveToken,
    newtext,
    cursorPosition: newActiveToken.start + newActiveToken.len,
  };
};

export {
  TOKEN_STARTER,
  tokenizedStringsAreEqual,
  updateMetadata,
  convertActiveToken,
  insertTokenStarter,
  splitTokenizedText,
  transformTokens,
};
